import React, { useState, ChangeEvent } from "react"
import { useTranslation, Trans } from "react-i18next"

import {
  Button,
  Title,
  Lead,
  Select,
  TextField,
  Icon,
} from "@terveystalo/design-system-react-components"
import authClient, { authConfig } from "./UserManager"
import { getBackend } from "./client"
import { config } from "./config"
import { useFormPersistence } from "./hooks/useFormPersistence"

const client = getBackend()

interface FormElement {
  value: string
}

// Type options for invoicing feedback
const InvoicingType = {
  feedback: "feedback",
  thank_you: "thank_you",
  inquiry: "inquiry",
} as const

type InvoicingTypeValue = (typeof InvoicingType)[keyof typeof InvoicingType]

interface InvoicingFormState {
  type?: InvoicingTypeValue
  description?: string
  visitDate?: string
  invoiceNumber?: string
  email?: string
}

const invoicingFormInitialState: InvoicingFormState = {
  type: undefined,
  description: "",
  visitDate: "",
  invoiceNumber: "",
  email: "",
}

interface InvoicingFormProps {
  isLoggedIn: boolean
}

function InvoicingForm({ isLoggedIn }: InvoicingFormProps) {
  const { t, i18n } = useTranslation()
  const { formState, setFormState, isRestoring } = useFormPersistence({
    formKey: "invoicingForm",
    initialState: invoicingFormInitialState,
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitMessage, setSubmitMessage] = useState("")
  const [lockedFields, setLockedFields] = useState<Set<string>>(new Set())
  const wrapperRef = React.useRef<HTMLDivElement>(null)

  // Pre-fill and lock fields from URL query parameters
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const urlType = params.get("type")
    const urlInvoiceNumber = params.get("invoiceNumber")

    const updates: Partial<InvoicingFormState> = {}
    const locked = new Set<string>()

    if (urlType && Object.values(InvoicingType).includes(urlType as InvoicingTypeValue)) {
      updates.type = urlType as InvoicingTypeValue
      locked.add("type")
    }

    if (urlInvoiceNumber) {
      updates.invoiceNumber = urlInvoiceNumber
      locked.add("invoiceNumber")
    }

    if (Object.keys(updates).length > 0) {
      setFormState((prev) => ({ ...prev, ...updates }))
      setLockedFields(locked)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Update page title when language changes
  React.useEffect(() => {
    document.title = t("pageTitleInvoicing")
  }, [i18n.language, t])

  // Debug logging for wrapper styles
  React.useEffect(() => {
    if (wrapperRef.current) {
      const computed = window.getComputedStyle(wrapperRef.current)
      console.log("InvoicingForm wrapper debug:", {
        width: computed.width,
        maxWidth: computed.maxWidth,
        marginLeft: computed.marginLeft,
        marginRight: computed.marginRight,
        classList: Array.from(wrapperRef.current.classList),
        viewportWidth: window.innerWidth,
      })
    }
  }, [])

  // Re-log on resize to see breakpoint changes
  React.useEffect(() => {
    const handleResize = () => {
      if (wrapperRef.current) {
        const computed = window.getComputedStyle(wrapperRef.current)
        console.log("InvoicingForm wrapper resize:", {
          width: computed.width,
          maxWidth: computed.maxWidth,
          viewportWidth: window.innerWidth,
        })
      }
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Disable scrolling when submit message modal is open
  React.useEffect(() => {
    if (submitMessage) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = ""
    }
  }, [submitMessage])

  // Type-safe mapping of invoicing type values to display labels
  const invoicingTypeLabels: Record<InvoicingTypeValue | "", string> = {
    "": t("invoicingForm.type.select"),
    [InvoicingType.feedback]: t("invoicingForm.type.feedback"),
    [InvoicingType.thank_you]: t("invoicingForm.type.thank_you"),
    [InvoicingType.inquiry]: t("invoicingForm.type.inquiry"),
  }

  const invoicingTypeOptions = Object.entries(invoicingTypeLabels).map(([value, label]) => ({
    label,
    value,
  }))

  const handleSelectChange = (name: keyof InvoicingFormState) => (e: ChangeEvent) => {
    const value = (e.target as unknown as FormElement).value
    setFormState((prevState) => ({
      ...prevState,
      [name]: value === "" ? undefined : value,
    }))
  }

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  // Determine if we should show visit date and invoice number fields
  // Only show these fields when the type is "feedback"
  const shouldShowInvoiceFields = formState.type === InvoicingType.feedback

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitMessage("")

    // Validation
    if (!formState.type) {
      setSubmitMessage(t("error.typeRequired"))
      setIsSubmitting(false)
      return
    }

    if (!formState.description) {
      setSubmitMessage(t("error.descriptionRequired"))
      setIsSubmitting(false)
      return
    }

    if (!formState.email) {
      setSubmitMessage(t("error.emailRequired"))
      setIsSubmitting(false)
      return
    }

    try {
      // Convert visitDate string to Date object if it exists
      const visitDate = formState.visitDate ? new Date(formState.visitDate) : undefined

      await client.submitInvoicingFeedback({
        type: formState.type,
        description: formState.description,
        visitDate,
        invoiceNumber: formState.invoiceNumber,
        email: formState.email,
      })

      setSubmitMessage(t("success.feedbackSubmitted"))
      // Reset form
      setFormState(invoicingFormInitialState)
    } catch (error) {
      console.error(error)
      setSubmitMessage(
        `${t("error.generic")}: ${error instanceof Error ? error.message : t("error.submitFailed")}`,
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  // Show loading state while restoring form data
  if (isRestoring) {
    return (
      <div className="min-h-screen bg-coal04 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lake mx-auto mb-4"></div>
          <p className="text-coal65">{t("form.loading")}</p>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="min-h-screen bg-coal04" style={{ position: "relative" }}>
        <div ref={wrapperRef} className="w-full m:max-w-7xl m:mx-auto">
          <header className="bg-woolhead shadow-sm border-b border-coal15">
            <div className="px-4 s:px-6 m:px-8 py-2">
              <Title>{t("invoicingForm.title")}</Title>
              <Lead>
                <Trans
                  i18nKey="invoicingForm.lead"
                  components={{
                    red: <span style={{ color: "#CE0F69" }} />,
                  }}
                />
              </Lead>
            </div>
          </header>
          <main className="s:px-6 m:px-8">
            <div
              className="bg-woolhead rounded-lg shadow p-2"
              style={{ backgroundColor: "#f8f9fa" }}
            >
              <Title as="h2" size="l" className="mb-2">
                {t("invoicingForm.formInfo")}
              </Title>

              {/* Bordered container with instructional text and type selection */}
              <div
                style={{
                  border: "1px solid #000",
                  borderRadius: "8px",
                  padding: "16px",
                  marginBottom: "16px",
                }}
              >
                <p style={{ marginBottom: "16px", color: "#333" }}>
                  {t("invoicingForm.typeInstructions")}
                </p>

                <Select
                  id="invoicing-type"
                  name="type"
                  label={t("invoicingForm.typeLabel")}
                  options={invoicingTypeOptions}
                  value={formState.type || ""}
                  onChange={handleSelectChange("type")}
                  required
                  disabled={lockedFields.has("type")}
                />
              </div>

              {/* Additional form fields */}
              <div className="space-y-2 mb-4">
                <TextField
                  id="description"
                  name="description"
                  label={t("invoicingForm.descriptionLabel")}
                  as="textarea"
                  value={formState.description}
                  onChange={handleInputChange}
                  rows={4}
                  required
                />

                {shouldShowInvoiceFields && (
                  <>
                    <TextField
                      id="visitDate"
                      name="visitDate"
                      type="date"
                      label={t("invoicingForm.visitDateLabel")}
                      value={formState.visitDate}
                      onChange={handleInputChange}
                    />

                    <TextField
                      id="invoiceNumber"
                      name="invoiceNumber"
                      label={t("invoicingForm.invoiceNumberLabel")}
                      value={formState.invoiceNumber}
                      onChange={handleInputChange}
                      required
                      disabled={lockedFields.has("invoiceNumber")}
                    />
                  </>
                )}

                <TextField
                  id="email"
                  name="email"
                  type="email"
                  label={t("invoicingForm.emailLabel")}
                  value={formState.email}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <p className="text-coal65 mb-6">
                {t("form.privacyNotice")}
                <a href={t("form.privacyStatementUrl")} className="link">
                  {t("form.privacyStatement")}
                </a>
                {t("form.privacyCustomers")}
              </p>

              {/* Debugging block - only shown when DEBUG_FORM_VALUES is set */}
              {config.debugFormValues && (
                <pre className="p-4 bg-coal15 text-coal85 rounded-md my-4">
                  <code>{JSON.stringify(formState, null, 2)}</code>
                </pre>
              )}

              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? t("form.submitting") : t("submit")}
              </Button>
            </div>
          </main>
        </div>

        {/* Success/Error Modal */}
        {submitMessage && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <div
              style={{
                backgroundColor: "#fff",
                padding: "32px",
                borderRadius: "8px",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                maxWidth: "500px",
                textAlign: "center",
              }}
            >
              <p style={{ marginBottom: "24px", fontSize: "1.125rem" }}>{submitMessage}</p>
              {isLoggedIn ? (
                <Button
                  type="button"
                  onClick={() =>
                    authClient.signoutRedirect({
                      post_logout_redirect_uri: authConfig.post_logout_redirect_uri,
                    })
                  }
                  variant="secondary"
                >
                  <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Icon name="LogOut" color="lake" />
                    {t("logout")}
                  </span>
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={() => (window.location.href = "https://www.terveystalo.com")}
                >
                  {t("close")}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </form>
  )
}

export default InvoicingForm
